// 📁 File Map - Visual Project Structure

import { useState, useEffect } from 'react';

interface FileNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  description: string;
  category: 'source' | 'config' | 'docs' | 'test' | 'generated';
  importance: 'required' | 'recommended' | 'optional';
  children?: FileNode[];
}

export function FileMap() {
  const [files, setFiles] = useState<FileNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'source' | 'config' | 'docs'>('all');
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set(['/']));

  const apiUrl = import.meta.env.VITE_API_URL || '';

  useEffect(() => {
    fetchFileStructure();
  }, []);

  const fetchFileStructure = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/files/structure`);
      const data = await response.json();

      if (data.success) {
        setFiles(data.data.files || getStaticFileStructure());
      } else {
        setFiles(getStaticFileStructure());
      }
    } catch (error) {
      console.error('Failed to fetch file structure:', error);
      setFiles(getStaticFileStructure());
    } finally {
      setLoading(false);
    }
  };

  const getStaticFileStructure = (): FileNode[] => {
    return [
      {
        name: 'apps',
        path: '/apps',
        type: 'directory',
        description: 'Application workspace - contains web frontend and API backend',
        category: 'source',
        importance: 'required',
        children: [
          {
            name: 'web',
            path: '/apps/web',
            type: 'directory',
            description: 'React frontend application with Vite',
            category: 'source',
            importance: 'required',
            children: [
              {
                name: 'src',
                path: '/apps/web/src',
                type: 'directory',
                description: 'Frontend source code',
                category: 'source',
                importance: 'required',
                children: [
                  {
                    name: 'App.tsx',
                    path: '/apps/web/src/App.tsx',
                    type: 'file',
                    description: 'Main app component with hash-based routing (#dashboard, #demos, #test)',
                    category: 'source',
                    importance: 'required',
                  },
                  {
                    name: 'main.tsx',
                    path: '/apps/web/src/main.tsx',
                    type: 'file',
                    description: 'React entry point - renders App into DOM',
                    category: 'source',
                    importance: 'required',
                  },
                  {
                    name: 'components',
                    path: '/apps/web/src/components',
                    type: 'directory',
                    description: 'React components library',
                    category: 'source',
                    importance: 'required',
                    children: [
                      {
                        name: 'Dashboard.tsx',
                        path: '/apps/web/src/components/Dashboard.tsx',
                        type: 'file',
                        description: 'Main dashboard - shows conversation history, health status, CTA banners',
                        category: 'source',
                        importance: 'required',
                      },
                      {
                        name: 'DemoScenarios.tsx',
                        path: '/apps/web/src/components/DemoScenarios.tsx',
                        type: 'file',
                        description: '4 business demo scenarios: Sales Rep, Absence Line, Phone Interviews, Inbound Sales',
                        category: 'source',
                        importance: 'recommended',
                      },
                      {
                        name: 'SystemTest.tsx',
                        path: '/apps/web/src/components/SystemTest.tsx',
                        type: 'file',
                        description: 'System verification page - tests all CloudFlare integrations (DB, KV, R2, AI)',
                        category: 'source',
                        importance: 'recommended',
                      },
                      {
                        name: 'SetupWizard.tsx',
                        path: '/apps/web/src/components/SetupWizard.tsx',
                        type: 'file',
                        description: 'First-time setup wizard for CloudFlare configuration',
                        category: 'source',
                        importance: 'optional',
                      },
                    ],
                  },
                ],
              },
              {
                name: 'package.json',
                path: '/apps/web/package.json',
                type: 'file',
                description: 'Frontend dependencies: React, Vite, Tailwind CSS, TypeScript',
                category: 'config',
                importance: 'required',
              },
              {
                name: 'vite.config.ts',
                path: '/apps/web/vite.config.ts',
                type: 'file',
                description: 'Vite build configuration',
                category: 'config',
                importance: 'required',
              },
              {
                name: 'tsconfig.json',
                path: '/apps/web/tsconfig.json',
                type: 'file',
                description: 'TypeScript compiler settings for frontend',
                category: 'config',
                importance: 'required',
              },
              {
                name: '.env',
                path: '/apps/web/.env',
                type: 'file',
                description: 'Environment variables: VITE_VAPI_PUBLIC_KEY, VITE_API_URL',
                category: 'config',
                importance: 'required',
              },
            ],
          },
          {
            name: 'api',
            path: '/apps/api',
            type: 'directory',
            description: 'Hono API backend on CloudFlare Workers',
            category: 'source',
            importance: 'required',
            children: [
              {
                name: 'src',
                path: '/apps/api/src',
                type: 'directory',
                description: 'Backend source code',
                category: 'source',
                importance: 'required',
                children: [
                  {
                    name: 'index.ts',
                    path: '/apps/api/src/index.ts',
                    type: 'file',
                    description: 'Main API - health, DB, KV, R2, AI, setup, validation endpoints',
                    category: 'source',
                    importance: 'required',
                  },
                  {
                    name: 'types.ts',
                    path: '/apps/api/src/types.ts',
                    type: 'file',
                    description: 'TypeScript type definitions for API and CloudFlare bindings',
                    category: 'source',
                    importance: 'required',
                  },
                  {
                    name: 'guards.ts',
                    path: '/apps/api/src/guards.ts',
                    type: 'file',
                    description: 'Validation guards for CloudFlare bindings',
                    category: 'source',
                    importance: 'required',
                  },
                ],
              },
              {
                name: 'tests',
                path: '/apps/api/tests',
                type: 'directory',
                description: 'API test suite',
                category: 'test',
                importance: 'recommended',
                children: [
                  {
                    name: 'endpoints.test.ts',
                    path: '/apps/api/tests/endpoints.test.ts',
                    type: 'file',
                    description: 'Integration tests for API endpoints',
                    category: 'test',
                    importance: 'recommended',
                  },
                  {
                    name: 'guards.test.ts',
                    path: '/apps/api/tests/guards.test.ts',
                    type: 'file',
                    description: 'Unit tests for validation guards',
                    category: 'test',
                    importance: 'recommended',
                  },
                ],
              },
              {
                name: 'wrangler.toml',
                path: '/apps/api/wrangler.toml',
                type: 'file',
                description: 'CloudFlare Workers config: bindings for D1, KV, R2, AI',
                category: 'config',
                importance: 'required',
              },
              {
                name: 'package.json',
                path: '/apps/api/package.json',
                type: 'file',
                description: 'Backend dependencies: Hono, Wrangler, Vitest',
                category: 'config',
                importance: 'required',
              },
            ],
          },
        ],
      },
      {
        name: 'docs',
        path: '/docs',
        type: 'directory',
        description: 'Project documentation',
        category: 'docs',
        importance: 'recommended',
        children: [
          {
            name: 'SALES-DEMO-GUIDE.md',
            path: '/docs/SALES-DEMO-GUIDE.md',
            type: 'file',
            description: 'Complete sales playbook for voice AI demos (4,500+ lines)',
            category: 'docs',
            importance: 'optional',
          },
          {
            name: 'VAPI-PHONE-INTEGRATION.md',
            path: '/docs/VAPI-PHONE-INTEGRATION.md',
            type: 'file',
            description: 'Phone number setup guide and cost analysis',
            category: 'docs',
            importance: 'optional',
          },
          {
            name: 'CLAUDE-CODE-PROMPTS.md',
            path: '/docs/CLAUDE-CODE-PROMPTS.md',
            type: 'file',
            description: 'Reusable prompts for extending the project',
            category: 'docs',
            importance: 'optional',
          },
        ],
      },
      {
        name: 'Configuration Files',
        path: '/',
        type: 'directory',
        description: 'Root configuration files',
        category: 'config',
        importance: 'required',
        children: [
          {
            name: 'package.json',
            path: '/package.json',
            type: 'file',
            description: 'Monorepo root - Turborepo workspace configuration',
            category: 'config',
            importance: 'required',
          },
          {
            name: 'turbo.json',
            path: '/turbo.json',
            type: 'file',
            description: 'Turborepo build pipeline configuration',
            category: 'config',
            importance: 'required',
          },
          {
            name: 'tsconfig.json',
            path: '/tsconfig.json',
            type: 'file',
            description: 'Root TypeScript configuration',
            category: 'config',
            importance: 'required',
          },
          {
            name: '.eslintrc.json',
            path: '/.eslintrc.json',
            type: 'file',
            description: 'ESLint code quality rules',
            category: 'config',
            importance: 'recommended',
          },
        ],
      },
      {
        name: 'Documentation',
        path: '/',
        type: 'directory',
        description: 'Project guides and references',
        category: 'docs',
        importance: 'recommended',
        children: [
          {
            name: 'README.md',
            path: '/README.md',
            type: 'file',
            description: 'Project overview and quick start guide',
            category: 'docs',
            importance: 'required',
          },
          {
            name: 'QUICK-START.md',
            path: '/QUICK-START.md',
            type: 'file',
            description: 'Fast setup instructions for new developers',
            category: 'docs',
            importance: 'recommended',
          },
          {
            name: 'DEPLOY-GUIDE.md',
            path: '/DEPLOY-GUIDE.md',
            type: 'file',
            description: 'Deployment instructions for CloudFlare',
            category: 'docs',
            importance: 'recommended',
          },
          {
            name: 'CHANGELOG.md',
            path: '/CHANGELOG.md',
            type: 'file',
            description: 'Version history and release notes',
            category: 'docs',
            importance: 'optional',
          },
        ],
      },
    ];
  };

  const toggleExpand = (path: string) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const getImportanceColor = (importance: string) => {
    switch (importance) {
      case 'required': return 'bg-red-100 text-red-700 border-red-200';
      case 'recommended': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'optional': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getIcon = (node: FileNode) => {
    if (node.type === 'directory') {
      return expandedPaths.has(node.path) ? '📂' : '📁';
    }

    const ext = node.name.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'tsx': case 'jsx': return '⚛️';
      case 'ts': case 'js': return '📜';
      case 'json': return '📋';
      case 'md': return '📝';
      case 'env': return '🔐';
      case 'toml': return '⚙️';
      default: return '📄';
    }
  };

  const renderFileTree = (nodes: FileNode[], level: number = 0) => {
    return nodes.map((node, index) => {
      const isExpanded = expandedPaths.has(node.path);
      const shouldShow = filter === 'all' || node.category === filter;

      if (!shouldShow && (!node.children || node.children.length === 0)) {
        return null;
      }

      return (
        <div key={node.path + index} className="mb-1">
          <div
            className={`flex items-start gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer transition ${
              level > 0 ? 'ml-' + (level * 4) : ''
            }`}
            onClick={() => node.type === 'directory' && toggleExpand(node.path)}
            style={{ marginLeft: `${level * 1.5}rem` }}
          >
            <span className="text-xl flex-shrink-0">{getIcon(node)}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-medium text-gray-900">
                  {node.name}
                </span>
                <span
                  className={`text-xs px-2 py-0.5 rounded border ${getImportanceColor(
                    node.importance
                  )}`}
                >
                  {node.importance}
                </span>
                <span className="text-xs text-gray-500 px-2 py-0.5 bg-gray-50 rounded">
                  {node.category}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">{node.description}</p>
            </div>
          </div>

          {node.children && isExpanded && (
            <div className="mt-1">{renderFileTree(node.children, level + 1)}</div>
          )}
        </div>
      );
    });
  };

  const stats = {
    total: 0,
    required: 0,
    recommended: 0,
    optional: 0,
  };

  const countFiles = (nodes: FileNode[]) => {
    nodes.forEach(node => {
      if (node.type === 'file') {
        stats.total++;
        if (node.importance === 'required') stats.required++;
        if (node.importance === 'recommended') stats.recommended++;
        if (node.importance === 'optional') stats.optional++;
      }
      if (node.children) countFiles(node.children);
    });
  };

  countFiles(files);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-4xl">📁</span>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">File Map</h1>
                <p className="text-sm text-gray-600">Visual project structure with AI descriptions</p>
              </div>
            </div>
            <a
              href="/"
              className="text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
              ← Back to Dashboard
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border-2 border-gray-200 p-4">
            <div className="text-xs text-gray-600 mb-1">Total Files</div>
            <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg border-2 border-red-200 p-4">
            <div className="text-xs text-red-600 mb-1">Required</div>
            <div className="text-3xl font-bold text-red-700">{stats.required}</div>
          </div>
          <div className="bg-white rounded-lg border-2 border-blue-200 p-4">
            <div className="text-xs text-blue-600 mb-1">Recommended</div>
            <div className="text-3xl font-bold text-blue-700">{stats.recommended}</div>
          </div>
          <div className="bg-white rounded-lg border-2 border-gray-200 p-4">
            <div className="text-xs text-gray-600 mb-1">Optional</div>
            <div className="text-3xl font-bold text-gray-700">{stats.optional}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border-2 border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Filter:</span>
            {(['all', 'source', 'config', 'docs'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded text-sm font-medium transition ${
                  filter === f
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* File Tree */}
        <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Project Structure</h2>
            <button
              onClick={() => setExpandedPaths(new Set(files.map(f => f.path)))}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium"
            >
              Expand All
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-2">⏳</div>
              <div className="text-gray-600">Loading file structure...</div>
            </div>
          ) : (
            <div className="space-y-1">{renderFileTree(files)}</div>
          )}
        </div>

        {/* Legend */}
        <div className="bg-white rounded-lg border-2 border-gray-200 p-6 mt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Legend</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-semibold text-gray-700 mb-2">Importance:</div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded border bg-red-100 text-red-700 border-red-200 text-xs">
                    required
                  </span>
                  <span className="text-gray-600">Critical - project won't work without it</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded border bg-blue-100 text-blue-700 border-blue-200 text-xs">
                    recommended
                  </span>
                  <span className="text-gray-600">Important - should be included</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded border bg-gray-100 text-gray-700 border-gray-200 text-xs">
                    optional
                  </span>
                  <span className="text-gray-600">Nice to have - can be removed</span>
                </div>
              </div>
            </div>
            <div>
              <div className="font-semibold text-gray-700 mb-2">Categories:</div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xl">⚛️</span>
                  <span className="text-gray-600">React/TypeScript source code</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xl">⚙️</span>
                  <span className="text-gray-600">Configuration files</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xl">📝</span>
                  <span className="text-gray-600">Documentation</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xl">🧪</span>
                  <span className="text-gray-600">Test files</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
